import { z } from 'zod'

import { recordPath, recordRoute } from '@/shared/registry'

import { questionTypes } from './assessment'

/** Where Find Assessment Responses and response details live. */
export const assessmentResponsesPath = '/launch/assessment-responses'
export const assessmentResponseRoute = recordRoute('launch', 'assessment-responses')

export function assessmentResponsePath(id: string): string {
  return recordPath('launch', 'assessment-responses', id)
}

/** Find Assessment Responses for one assessment, as Assessment Details' Find Responses opens it. */
export function assessmentResponsesFor(assessmentId: string): string {
  return `${assessmentResponsesPath}?${new URLSearchParams({ filter: `assessmentId:in:${assessmentId}` }).toString()}`
}

/** The API's search, and one response's details. */
export const assessmentResponsesApi = '/launch/v1/assessment-responses'

export function assessmentResponseDetailsApi(id: string): string {
  return `${assessmentResponsesApi}/${encodeURIComponent(id)}/details`
}

/** What finding and reading responses needs. */
export const readAssessmentResponsesPermission = 'launch.assessment-responses.read'

const responseSchema = z.object({
  id: z.string(),
  learnerId: z.string(),
  learnerName: z.string().nullish(),
  externalId: z.string().nullish(),
  assessmentId: z.string(),
  assessmentName: z.string().nullish(),
  activityId: z.string(),
  courseId: z.string(),
  courseName: z.string().nullish(),
  classId: z.string().nullish(),
  className: z.string().nullish(),
  organizationId: z.string(),
  organizationName: z.string().nullish(),
  scorePercent: z.number().int(),
  submittedOn: z.string(),
})

/** An assessment response as Find Assessment Responses lists it. */
export type AssessmentResponse = z.infer<typeof responseSchema>

export function parseAssessmentResponse(item: unknown): AssessmentResponse {
  return responseSchema.parse(item)
}

const detailsSchema = z.object({
  response: responseSchema,
  questions: z.array(
    z.object({
      id: z.string(),
      type: z.enum(questionTypes),
      text: z.string(),
      moreInfoUrl: z.string().nullish(),
      isRight: z.boolean(),
      explanation: z.string().nullish(),
      answers: z.array(z.object({ id: z.string(), text: z.string(), isChosen: z.boolean(), isCorrect: z.boolean() })),
    }),
  ),
})

/** One response with each question answered, its answers marked chosen and correct. */
export type AssessmentResponseDetails = z.infer<typeof detailsSchema>

export function parseAssessmentResponseDetails(body: unknown): AssessmentResponseDetails {
  return detailsSchema.parse(body)
}
